import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';

import { HTML } from '~/core/helpers/html.helper';

import { WindowService } from '~/core/services/window/window.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

export const GTM_ID = new InjectionToken<string>('Gtm ID');

declare global {
  interface Window {
    dataLayer: object[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class TagManagerService {
  private loaded = false;

  public readonly toggleNameEnum = ToggleNameEnum;

  constructor(
    @Inject(GTM_ID) private readonly gtmId: string,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly windowService: WindowService,
    private readonly toggleService: ToggleService
  ) {}

  public get isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * @see https://developers.google.com/analytics/devguides/collection/app-web/disable-analytics
   */
  private disable(): void {
    this.windowService.nativeWindow[`ga-disable-${this.gtmId}`] = true;
  }

  private enable(): void {
    this.windowService.nativeWindow[`ga-disable-${this.gtmId}`] = false;
  }

  private loadScripts(): void {
    const script = this.document.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window, document,'script','dataLayer','${this.gtmId}');`;

    HTML.unshift(this.document.head, script);

    const iframe = this.document.createElement('iframe');
    iframe.src = `//www.googletagmanager.com/ns.html?id=${this.gtmId}`;
    iframe.title = 'google tag manager frame';
    const noscript = this.document.createElement('noscript');
    noscript.appendChild(iframe);

    HTML.unshift(this.document.body, noscript);
  }

  private push(data: object): void {
    if (isPlatformServer(this.platformId) || !this.isLoaded) {
      return;
    }

    window.dataLayer.push(data);
  }

  public initialize(): void {
    if (isPlatformServer(this.platformId) || this.isLoaded) {
      return;
    }

    if (this.isTagManagerEnabled()) {
      this.loadScripts();
      this.loaded = true;
      const window = this.windowService.nativeWindow;
      window.dataLayer = window.dataLayer || [];
    }
  }

  public pushNoResult(category: string): void {
    this.push({
      event: 'No_result',
      eventCategory: category,
      eventAction: 'No result',
      eventLabel: 'No result',
    });
  }

  public isTagManagerEnabled(): boolean {
    return this.toggleService.isEnabled(this.toggleNameEnum.GoogleTagManager);
  }
}
